import { finalize } from 'rxjs/operators';
import {
	Component,
	OnInit,
	Input,
	Output,
	ContentChildren,
	forwardRef,
	ViewEncapsulation,
	Injectable,
	EventEmitter
} from '@angular/core';
import {
	Validator,
	AbstractControl,
	FormGroup,
	FormArray,
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
	NG_VALIDATORS
} from '@angular/forms';
import { Observable } from 'rxjs';

@Injectable()
export class SelectValidator implements Validator {
	options: {
		required?: boolean;
		minModelSize?: number;
	} = {};

	validate(c: AbstractControl) {
		if (!this.options) { return null; }

		const errors: any = {};

		if (this.options.required && (!c.value || (c.value instanceof Array) && c.value.length == 0)) {
			errors.required = true;
		}

		if (this.options.minModelSize && (!c.value || (c.value instanceof Array) && c.value.length < this.options.minModelSize)) {
			errors.minModelSize = true;
		}

		return Object.keys(errors).length > 0 ? errors : null;
	}
}

@Injectable()
export class SelectValueAccessor implements ControlValueAccessor {
	modelWrites = new EventEmitter<any>();
	trackBy: string | ((item: any) => string);
	valueBy: string | ((item: any) => string);

	private _model: any;
	private onChange: (m: any) => void;
	private onTouched: (m: any) => void;

	writeValue(value: any): void {
		this._model = value;
		this.modelWrites.emit(value);
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	get model() {
		return this._model;
	}

	set(value: any) {
		this._model = value;
		this.onChange(this._model);
	}

	add(value: any) {
		if (!this.has(value)) {
			if (this._model instanceof Array) {
				this._model.push(value);
			} else {
				this._model = [value];
			}
			this.onChange(this._model);
		}
	}

	remove(value: any) {
		if (this.trackBy) {
			const item = this._model.find((i: any) => {
				return this.extractValue(i, this.trackBy) == this.extractValue(value, this.trackBy);
			});
			this.removeAt(this._model.indexOf(item));
		} else {
			const item = this._model.find((i: any) => {
				return i == value;
			});
			this.removeAt(this._model.indexOf(item));
		}
	}

	removeAt(index: number): boolean {
		if (!this._model || index < 0 || (index > this._model.length - 1)) {
			return false;
		}

		this._model.splice(index, 1);
		this.onChange(this._model);
	}

	clear() {
		if (this._model instanceof Array) {
			this._model.splice(0, this._model.length);
		} else {
			this._model = undefined;
		}
	}

	addAt(value: any, index: number): boolean {
		if (!this._model || index < 0) {
			return false;
		}

		this._model.splice(index, 0, value);
		this.onChange(this._model);
	}

	addOrRemove(value: any) {
		if (this.has(value)) {
			this.remove(value);
		} else {
			this.add(value);
		}
	}

	has(value: any): boolean {
		if (this._model instanceof Array) {
			if (this.trackBy) {
				return !!this._model.find((i: any) => {
					return this.extractValue(i, this.trackBy) == this.extractValue(value, this.trackBy);
				});
			} else {
				return !!this._model.find((i: any) => {
					return i == value;
				});
			}

		} else if (this._model != null && this._model != undefined) {
			if (this.trackBy) {
				return this.extractValue(this._model, this.trackBy) == this.extractValue(value, this.trackBy);
			} else {
				return this._model == value;
			}
		}

		return false;
	}

	addMany(values: any[]): void {
		if (!values || !values.length) { return; }
		values.forEach(value => this.add(value));
	}

	removeMany(values: any[]): void {
		if (!values || !values.length) { return; }
		values.forEach(value => this.remove(value));
	}

	hasMany(values: any[]): boolean {
		if (!values || !values.length) { return false; }

		for (let i = 0; i < values.length; i++) {
			if (!this.has(values[i].value)) {
				return false;
			}
		}

		return true;
	}

	private extractModelValue(model: any) {
		if (this.valueBy) {
			return this.extractValue(model, this.valueBy);
		} else {
			return model;
		}
	}

	private extractValue(model: any, value: string | ((item: any) => string)) {
		if (value instanceof Function) {
			return (value as (item: any) => any)(model);
		} else {
			return model[value as string];
		}
	}
}

export class FormHelper {
	public static toggle(control: AbstractControl, disable?: boolean, config?: { onlySelf?: boolean; emitEvent?: boolean; }) {
		if (control != null) {
			if (disable == null) {
				disable = !control.disabled;
			}

			try {
				disable ? control.disable(config) : control.enable(config);
			}
			catch (e) {
				console.warn(e);
			}
			finally { }
		}
	}

	public static toggleMore(names: string[], control: FormGroup, disable?: boolean) {
		for (let name of names) {
			FormHelper.toggle(control.get(name), disable);
		}
	}

	public static patchFormArray<TType>(array: FormArray, values: TType[], factory: PatchFormArrayDelegate<TType>) {
		for (let i = 0; i < values.length; ++i) {
			let control: AbstractControl = array.at(i);
			let value = values[i];

			if (control == null) {
				control = factory(value);
				array.push(control);
			}
			else {
				control.patchValue(value);
			}
		}

		for (let i = values.length; i < array.controls.length; ++i) {
			array.removeAt(i);
		}
	}
}

type PatchFormArrayDelegate<TType> = (value: TType) => AbstractControl;

export abstract class SmComponentBase implements OnInit {
	form: FormGroup;
	loading: boolean = true;

	ngOnInit() {
		this.buildForm();
		this.load();
	}

	protected do(observable: Observable<any>): Observable<any> {
		this.startLoading();
		return observable
			.pipe(finalize(() => this.stopLoading()));
	}

	protected abstract load();

	protected abstract buildForm(): void;

	protected startLoading() {
		this.loading = true;
	}

	protected stopLoading() {
		this.loading = false;
	}
}


// WEBPACK FOOTER //
// ./node_modules/angular-router-loader/src!./node_modules/angular2-template-loader!./app/core/forms.ts